import axios from 'axios';
import store from '../store';
import {
  CHANNEL_APPLICATION_STATUS_APPLYING,
  CHANNEL_APPLICATION_STATUS_APPROVED,
  CHANNEL_APPLICATION_STATUS_REJECTED,
  CHANNEL_APPLICATION_STATUS_UNAPPLIED,
} from '../plugins/constants';

// #301: CS審査ステータスによる画面遷移制御
//
// 優先順位: requiresAuth → application_guard → guideline_guard → 通常遷移
// 未ログイン状態で本ガードを評価すると例外となるため、呼び出し側で必ず
// isLogin() を先に通すこと。

// 1セッション中に1回だけ /channel-applications/me/ を叩くためのフラグ
let _fetched = false;

/**
 * 1セッションにつき1回 /channel-applications/me/ を叩き、store の applicationStatus を
 * サーバー側の最新値で上書きする。localStorage にキャッシュされた古い値（例: 却下後に
 * 承認された場合）を是正する役割も兼ねる。
 *
 * API エラー時は既存値を維持し、通常遷移を許可する（既存ユーザーの締め出し防止）。
 */
export async function ensureApplicationStatus() {
  if (_fetched) return;
  _fetched = true;

  try {
    const apiEndpoint = process.env.VUE_APP_API_ENDPOINT;
    const response = await axios.get(`${apiEndpoint}/channel-applications/me/`, {
      headers: {
        'Authorization': `Bearer ${store.state.accessToken}`,
      },
    });
    if (response.status === 200) {
      const data = response.data;
      store.commit('setApplicationStatus', data.application_status);
      if (data.show_approval_alert !== undefined) {
        store.commit('setShowApprovalAlert', data.show_approval_alert);
      }
    }
  } catch (e) {
    // API エラー時は既存値を維持して通常遷移 (既存ユーザーの締め出し防止)
    console.error('ensureApplicationStatus: API call failed', e);
  }
}

/**
 * テスト用: _fetched フラグをリセットする
 */
export function _resetFetchedFlag() {
  _fetched = false;
}

// ガード対象外ルート (無限リダイレクト防止)
// channel-application/* 系は _resolveCorrectPath で個別ハンドリングするため含めない
const EXEMPT_ROUTE_NAMES = new Set([
  'Login',
  'AccountCreation',
  'AccountCreated',
  'UserGuideline',
  'Information',
  'InformationDetails',
]);

/**
 * ステータスに対応する正しい channel-application ページパスを返す。
 * 承認済み・該当なしの場合は null。
 */
function _resolveCorrectPath(applicationStatus, waitForInvitation) {
  if (applicationStatus === null || applicationStatus === undefined) {
    if (waitForInvitation) return '/channel-application/waiting';
    return '/account-created';
  }
  switch (Number(applicationStatus)) {
    case CHANNEL_APPLICATION_STATUS_UNAPPLIED:
    case CHANNEL_APPLICATION_STATUS_APPLYING:
      return '/channel-application/pending';
    case CHANNEL_APPLICATION_STATUS_REJECTED:
      return '/channel-application/rejected';
    default:
      return null;
  }
}

/**
 * 承認ステータスに応じた遷移先を判定する
 * @param {import('vue-router').RouteLocationNormalized} to
 * @returns {string|null} リダイレクト先パス。遷移抑止不要なら null
 */
export function resolveApplicationRedirect(to) {
  const applicationStatus = store.state.applicationStatus;
  const isChannelApplicationPath = typeof to?.path === 'string' && to.path.startsWith('/channel-application/');

  // --- channel-application/* 配下のステータス整合性チェック ---
  if (isChannelApplicationPath) {
    // 承認済み → 通常画面へ
    if (Number(applicationStatus) === CHANNEL_APPLICATION_STATUS_APPROVED) {
      return '/article-management';
    }
    // フォーム画面は能動的にアクセスする画面なのでチェック対象外
    if (to.name === 'ChannelApplicationForm') {
      return null;
    }
    // ステータスと画面の不一致を検知してリダイレクト
    const correctPath = _resolveCorrectPath(applicationStatus, store.state.waitForInvitation);
    if (correctPath && correctPath !== to.path) {
      return correctPath;
    }
    return null;
  }

  // --- 通常ルート ---

  if (to?.name && EXEMPT_ROUTE_NAMES.has(to.name)) {
    return null;
  }

  // メンバーとして参加しているチャンネルがあれば、自チャンネルの審査状態に関係なく通す
  // (例: 自チャンネル未登録 or 保留/却下でも、編集メンバーとして参加済みなら通常画面を使える)
  const hasMembership = store.state.hasMembership;
  if (hasMembership) {
    return null;
  }

  // ChannelApplication が存在しない (null/undefined) ユーザー
  if (applicationStatus === null || applicationStatus === undefined) {
    const waitForInvitation = store.state.waitForInvitation;
    if (waitForInvitation) {
      return '/channel-application/waiting';
    }
    return '/account-created';
  }

  switch (Number(applicationStatus)) {
    case CHANNEL_APPLICATION_STATUS_UNAPPLIED:
    case CHANNEL_APPLICATION_STATUS_APPLYING:
      return '/channel-application/pending';
    case CHANNEL_APPLICATION_STATUS_REJECTED:
      return '/channel-application/rejected';
    case CHANNEL_APPLICATION_STATUS_APPROVED:
    default:
      return null;
  }
}
